import { useState } from 'react';
import { toast } from 'sonner';
import { Search, Loader2, Square, Wand2, X, Plus } from 'lucide-react';
import { leakSearch, measFromSim, srcFits, measCfg } from '../lib/aquaLeakFind';
import { NightBox, MeasPlan } from './AquaMeasPlan';
import { LoggerImport } from './AquaLogger';
import { loggerValue } from '../lib/profImport';
import { cancelSim } from '../lib/aquaNet';
import { useAqua, setAqua } from '../lib/aquaStore';
import { noiseNear, lm } from '../lib/leakMatch';

const nf = (v, d = 2) => (v == null || !Number.isFinite(+v) ? '–' : (+v).toFixed(d).replace('.', ','));
const nm = (t, f) => f?.name || `${t['fit_' + f?.type] || f?.type} ${String(f?.id || '').slice(-4)}`;
let stopFlag = false;

function MeasInputs({ t, project, ls, setLs }) {
  const F = project.fittings || [], hyd = F.filter((f) => f.type === 'hydrant'), used = Object.keys(ls.hyd || {});
  const put = (k, id, v) => setLs({ [k]: { ...(ls[k] || {}), [id]: v } });
  const drop = (id) => { const h = { ...(ls.hyd || {}) }, lg = { ...(ls.log || {}) }; delete h[id]; delete lg[id]; setLs({ hyd: h, log: lg }); };
  return (
    <>
      <b className="aq-sub">{t.lfSrc}</b>
      <small className="muted">{t.lfSrcHint}</small>
      {srcFits(project).map((f) => <label key={f.id} className="aq-row">{nm(t, f)}<input type="number" step="0.01" data-testid={`lf-src-${f.id}`} value={ls.src?.[f.id] ?? ''} onChange={(e) => put('src', f.id, e.target.value)} /> l/s</label>)}
      <b className="aq-sub">{t.lfHyd} ({used.length})</b>
      {used.map((id) => <label key={id} className="aq-row">{nm(t, F.find((f) => f.id === id))}<input type="number" step="0.01" data-testid={`lf-hyd-${id}`} value={ls.hyd[id] ?? ''} onChange={(e) => put('hyd', id, e.target.value)} /> bar{ls.log?.[id] && <span className="plog-badge" data-testid={`lf-hyd-log-${id}`} title={ls.log[id].file}>Logger</span>}<button className="icon-btn" data-testid={`lf-hyd-del-${id}`} aria-label="x" onClick={() => drop(id)}><X size={14} /></button></label>)}
      <label className="aq-row"><Plus size={14} /><select data-testid="lf-hyd-add" value="" onChange={(e) => e.target.value && put('hyd', e.target.value, '')}><option value="">{t.lfHydAdd}</option>{hyd.filter((f) => !used.includes(f.id)).map((f) => <option key={f.id} value={f.id}>{nm(t, f)}</option>)}</select></label>
      <LoggerImport t={t} project={project} ls={ls} setLs={setLs} />
    </>
  );
}

function Ranking({ t, r, pipes, project }) {
  if (r.error) return <p className="aq-err" data-testid="lf-error">{t['lfErr_' + r.error] || t.aqErr_calc}</p>;
  return (
    <div data-testid="lf-result">
      <p className="lf-kpi"><b data-testid="lf-qleak">{t.lfQLeak}: {nf(r.qLeak)} l/s ({nf(r.qLeak * 3.6, 1)} m³/h)</b> · {t.lfMeasSum} {nf(r.meas)} · {t.lfSimSum} {nf(r.base)} l/s</p>
      {r.none && <p className="muted" data-testid="lf-none">{t.lfNone}</p>}
      {!r.none && !r.discr && <p className="aq-warn" data-testid="lf-nodiscr">{t.lfNoDiscr}</p>}
      {!r.none && r.flat && <p className="aq-warn" data-testid="lf-flat">{t.lfFlat}</p>}
      {!r.none && r.top?.length > 0 && (
        <ol className="lf-top" data-testid="lf-top">
          {r.top.map((l, i) => (
            <li key={l.id}><button className="lf-hit" data-testid={`lf-top-${i}`} data-mid={l.mid.join(',')} onClick={() => { window.__gissMapView?.()?.m?.setView(l.mid, 19); if (window.innerWidth < 760) window.dispatchEvent(new Event('giss-aqua-hide')); }}>
              <i style={{ background: l.rel > 0.66 ? '#dc2626' : l.rel > 0.33 ? '#f97316' : '#facc15' }} />
              <span>{pipes[l.pipeId]?.name || l.id} · DN {Math.round(l.d)} · {nf(l.len, 0)} m{noiseNear(project, l.pipeId) && <em className="lf-noise" data-testid={`lf-top-noise-${i}`}> · 🔊 {lm().noise}</em>}</span><b>{nf(l.prob * 100, 1)} %</b>
            </button></li>
          ))}
        </ol>
      )}
      {r.runs > 0 && <small className="muted">{t.lfRuns.replace('{n}', r.runs)}</small>}
    </div>
  );
}

// leak search: measured inflow per supply + pressures at hydrants -> most likely pipe sections (ranking + map colours)
export function AquaLeakFind({ t, project, cfg, setCfg }) {
  const s = useAqua(), ls = cfg.ls || {}, [busy, setBusy] = useState(0);
  // changing measuring time / day type re-reads the logger values
  const setLs = (p) => {
    const n = { ...ls, ...p };
    if (('hour' in p || 'day' in p) && n.log) n.hyd = { ...(n.hyd || {}), ...Object.fromEntries(Object.entries(n.log).filter(([id]) => n.hyd && id in n.hyd).map(([id, lg]) => [id, loggerValue(lg, n.day, n.hour) ?? ''])) };
    setCfg({ ls: n });
  };
  const pipes = Object.fromEntries((project.pipes || []).map((p) => [p.id, p]));
  const fromSim = async () => {
    let ids = Object.keys(ls.hyd || {});
    if (!ids.length) { const H = (project.fittings || []).filter((f) => f.type === 'hydrant'); ids = H.filter((_, i) => i % Math.max(1, Math.round(H.length / 8)) === 0).slice(0, 8).map((f) => f.id); }
    const m = await measFromSim(project, measCfg(cfg), ids);
    if (m.error) { toast.error(t.aqHydCalcErr); return; }
    setLs({ ...m, log: Object.fromEntries(Object.entries(ls.log || {}).filter(([id]) => !(id in m.hyd))) }); toast.success(t.lfFromSimOk);
  };
  const run = async () => {
    stopFlag = false; setBusy(1);
    try {
      const r = await leakSearch(project, measCfg(cfg), ls, (n) => setBusy(n + 1), () => stopFlag);
      if (r.error !== 'cancel') setAqua({ ls: r });
      if (r.top?.length) setCfg({ lfTop: { at: r.at, top: r.top.map((x) => ({ pipeId: x.pipeId, prob: x.prob, coords: x.coords })) } });
    } catch (e) { toast.error(String(e?.message || e)); }
    setBusy(0);
  };
  const stop = () => { stopFlag = true; cancelSim(); };
  return (
    <div className="aq-leakfind" data-testid="lf-panel">
      <h4>{t.lfTitle}</h4>
      <p className="muted">{t.lfIntro}</p>
      <NightBox t={t} ls={ls} setLs={setLs} cfg={cfg} setCfg={setCfg} />
      <MeasPlan t={t} project={project} cfg={measCfg(cfg)} ls={ls} setLs={setLs} qGuess={s.ls?.qLeak} />
      <MeasInputs t={t} project={project} ls={ls} setLs={setLs} />
      <div className="row">
        <button className="btn btn-outline btn-sm" data-testid="lf-from-sim" disabled={!!busy || (cfg.train && !cfg.train.done)} onClick={fromSim}><Wand2 size={14} /> {t.lfFromSim}</button>
        <button className="btn btn-accent btn-sm" data-testid="lf-run" disabled={!!busy} onClick={run}>{busy ? <Loader2 size={14} className="spin" /> : <Search size={14} />} {busy ? `${t.lfBusy} ${busy - 1}` : t.lfRun}</button>
        {busy > 0 && <button className="btn btn-outline btn-sm" data-testid="lf-cancel" onClick={stop}><Square size={14} /> {t.aqCancel}</button>}
        {s.ls && <button className="btn btn-outline btn-sm" data-testid="lf-clear" onClick={() => setAqua({ ls: null })}><X size={14} /> {t.lfClear}</button>}
      </div>
      {s.ls && <Ranking t={t} r={s.ls} pipes={pipes} project={project} />}
    </div>
  );
}
